import { useIsNewSessionListEnabled } from '@/app-shell/flags/session-flags';
import { sidebarOpenTabAtom, SidebarTab } from '@/atoms/persistedPreferencesAtoms';
import {
  ASK_HEIDI_BAR_WIDTH,
  SESSIONS_BAR_WIDTH,
  useAskHeidiBarWidth,
  useSessionsBarWidth,
} from '@/atoms/sessionsBarAtoms';
import type { ImperativePanelHandle } from '@heidi/design-system/resizable';
import { useContainerWidth, useIsMobile } from '@heidi/utils';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** ID for the layout panel group container element, used by ResizeObserver */
export const LAYOUT_PANEL_GROUP_CONTAINER_ID = 'layout-panel-group-container';

export type ActiveSidebarTab = 'sessions' | 'askheidi' | 'none';

export interface UseResizableSideBarsConfig {
  /** Pixel minimum width for the sessions sidebar */
  sessionsMinWidthPx?: number;
  /** Pixel minimum width for the ask heidi sidebar */
  askHeidiMinWidthPx?: number;
}

export interface UseResizableSideBarsReturn {
  activeTab: ActiveSidebarTab;
  storedPanelWidth: number;
  handleResize: (size: number) => void;
  // Panel ref for imperative control
  sidebarPanelRef: React.RefObject<ImperativePanelHandle>;
  // Panel configuration
  panelConfig: {
    defaultSize: number;
    minSize: number;
    maxSize: number;
  };
  isDragging: boolean;
  handleDragging: (isDragging: boolean) => void;
}

/**
 * Custom hook that encapsulates all resizable Sidebar logic
 * Handles visibility, width management, and panel configuration for different sidebar tabs
 */
export const useResizableSideBars = (
  config: UseResizableSideBarsConfig = {}
): UseResizableSideBarsReturn => {
  const { sessionsMinWidthPx, askHeidiMinWidthPx } = config;
  const isMobile = useIsMobile();
  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  // Under the new session list, the legacy SESSION_LIST tab has no visible
  // content: ResizableMenuPanels gates its aside off entirely. Any dispatcher
  // setting the tab would open an empty ResizablePanel — neutralize the read
  // here rather than hunting every setSidebarOpenTab(SESSION_LIST) call site.
  const isSidebarSessionListOpen =
    !isNewSessionListEnabled && sidebarOpenTab === SidebarTab.SESSION_LIST;
  const isSidebarAskHeidiOpen = sidebarOpenTab === SidebarTab.ASK_HEIDI_V2;
  const router = useRouter();

  // Treat Ask Heidi (evidence) sidebar as closed on non-session routes.
  // This ensures the evidence sidebar never shows on routes like /scribe/chat.
  const isSessionRoute = router.pathname.startsWith('/scribe/session/');
  const isSessionListRoute = router.pathname.startsWith('/scribe/sessions');

  // Determine active tab based on sidebar state
  const getActiveTab = (): ActiveSidebarTab => {
    if (isMobile) return 'none'; // On mobile, sidebar is handled by mobile header sheet
    if (isSessionListRoute) return 'none'; // Hide sidebar on session list page
    if (isSidebarSessionListOpen) return 'sessions';
    if (isSidebarAskHeidiOpen && isSessionRoute) return 'askheidi';
    return 'none';
  };

  const activeTab = getActiveTab();
  const shouldShow = activeTab !== 'none';

  const { width: storedSessionsWidth, setWidth: setSessionsBarWidth } =
    useSessionsBarWidth();
  const { width: storedAskHeidiWidth, setWidth: setAskHeidiBarWidth } =
    useAskHeidiBarWidth();
  const sidebarPanelRef = useRef<ImperativePanelHandle>(null);
  const [isDragging, setIsDragging] = useState(false);
  const latestSizeRef = useRef<number>(0);
  const containerWidth = useContainerWidth(LAYOUT_PANEL_GROUP_CONTAINER_ID);

  // Get the appropriate stored width based on active tab
  const storedPanelWidth =
    activeTab === 'sessions' ? storedSessionsWidth : storedAskHeidiWidth;

  // Handle show/hide with collapse/expand methods
  useEffect(() => {
    if (sidebarPanelRef.current && !isDragging) {
      if (shouldShow) {
        sidebarPanelRef.current.resize(storedPanelWidth);
      } else {
        sidebarPanelRef.current.collapse();
      }
    }
  }, [shouldShow, isDragging, storedPanelWidth]);

  // Store the latest size during dragging, only save when dragging stops
  const handleResize = useCallback((size: number) => {
    if (size > 0) {
      // Store the latest size for save when dragging stops
      latestSizeRef.current = size;
    }
  }, []);

  const handleDragging = useCallback(
    (dragging: boolean) => {
      setIsDragging(dragging);

      // When dragging stops, save the final value to the appropriate store
      if (!dragging && latestSizeRef.current > 0) {
        if (activeTab === 'sessions') {
          const constrainedSize = Math.max(latestSizeRef.current, SESSIONS_BAR_WIDTH.MIN);
          setSessionsBarWidth(constrainedSize);
        } else if (activeTab === 'askheidi') {
          const constrainedSize = Math.max(
            latestSizeRef.current,
            ASK_HEIDI_BAR_WIDTH.MIN
          );
          setAskHeidiBarWidth(constrainedSize);
        }
        latestSizeRef.current = 0; // Reset
      }
    },
    [activeTab, setSessionsBarWidth, setAskHeidiBarWidth]
  );

  // Memoize panel configuration to prevent unnecessary re-renders.
  // When pixel minimums are provided, dynamically compute the percentage-based
  // minSize so the panel never shrinks below the pixel threshold on any screen size.
  const panelConfig = useMemo(() => {
    const staticMin =
      activeTab === 'sessions' ? SESSIONS_BAR_WIDTH.MIN : ASK_HEIDI_BAR_WIDTH.MIN;
    const maxSize =
      activeTab === 'sessions' ? SESSIONS_BAR_WIDTH.MAX : ASK_HEIDI_BAR_WIDTH.MAX;

    let minSize: number = staticMin;

    if (containerWidth > 0) {
      const pixelMin = activeTab === 'sessions' ? sessionsMinWidthPx : askHeidiMinWidthPx;
      if (pixelMin) {
        const dynamicMin = (pixelMin / containerWidth) * 100;
        // Use the larger of dynamic or static min, but cap at maxSize
        minSize = Math.min(Math.max(dynamicMin, staticMin), maxSize);
      }
    }

    return {
      defaultSize: storedPanelWidth,
      minSize,
      maxSize,
    };
  }, [
    storedPanelWidth,
    activeTab,
    containerWidth,
    sessionsMinWidthPx,
    askHeidiMinWidthPx,
  ]);

  // Save final value on unmount if there's a pending size
  useEffect(() => {
    return () => {
      if (latestSizeRef.current > 0) {
        if (activeTab === 'sessions') {
          const constrainedSize = Math.max(latestSizeRef.current, SESSIONS_BAR_WIDTH.MIN);
          setSessionsBarWidth(constrainedSize);
        } else if (activeTab === 'askheidi') {
          const constrainedSize = Math.max(
            latestSizeRef.current,
            ASK_HEIDI_BAR_WIDTH.MIN
          );
          setAskHeidiBarWidth(constrainedSize);
        }
      }
    };
  }, [activeTab, setSessionsBarWidth, setAskHeidiBarWidth]);

  return {
    activeTab,
    storedPanelWidth,
    handleResize,
    sidebarPanelRef,
    panelConfig,
    isDragging,
    handleDragging,
  };
};
